import React, { useState, useEffect } from "react";
import { api, fmtCurrency, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/Primitives";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Check, CreditCard } from "lucide-react";

function loadRazorpay() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load Razorpay. Check your connection."));
    document.body.appendChild(s);
  });
}

export default function Subscription() {
  const [d, setD] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const load = () => api.get("/subscription").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const changePlan = async (planId) => {
    try { await api.put("/company", { plan_id: planId }); toast.success("Plan updated"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const payNow = async (planId) => {
    try {
      const { data } = await api.post("/payments/razorpay/order", { plan_id: planId, billing_cycle: cycle });
      await loadRazorpay();
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
        name: data.company_name, description: `${planId.replace("plan_", "").toUpperCase()} ${cycle} subscription`,
        prefill: { name: data.name, email: data.email, contact: data.contact },
        theme: { color: "#0284c7" },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful — your plan is now active!");
            load();
          } catch (e) { toast.error(errMsg(e)); }
        },
      });
      rzp.on("payment.failed", () => toast.error("Payment failed. Please try again."));
      rzp.open();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!d) return <div className="py-16 flex justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-sky-500" /></div>;

  const limits = { users: d.plan?.max_users || 0, products: d.plan?.max_products || 0, leads: d.plan?.max_leads || 0 };
  const bar = (used, max) => Math.min((used / (max || 1)) * 100, 100);

  return (
    <div>
      <PageHeader title="Subscription" subtitle="Your plan, usage and billing" />

      <Card className="border border-slate-200/80 rounded-xl shadow-sm p-6 mb-6 bg-slate-900 text-white">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-xl bg-sky-500/20 flex items-center justify-center"><CreditCard className="h-7 w-7 text-sky-400" /></div>
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-400">Current plan</p>
              <p className="text-2xl font-bold font-display">{d.plan?.name} · {fmtCurrency(d.company?.subscription_cost)}/mo</p>
            </div>
          </div>
          <div className="text-right">
            <StatusBadge value={d.company?.status} />
            <p className="text-xs text-slate-400 mt-2">Billing status: {d.company?.subscription_status}</p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {[["Users", d.usage.users, limits.users], ["Products", d.usage.products, limits.products], ["Leads", d.usage.leads, limits.leads]].map(([label, used, max]) => (
          <Card key={label} className="border border-slate-200/80 rounded-xl shadow-sm p-5" data-testid={`usage-${label.toLowerCase()}`}>
            <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</p>
            <p className="text-xl font-bold font-display mt-1">{used} <span className="text-sm font-normal text-slate-400">/ {max >= 9999 ? "∞" : max}</span></p>
            <Progress value={bar(used, max)} className="mt-3 h-2" />
          </Card>
        ))}
      </div>

      <h3 className="text-lg font-semibold text-slate-800 font-display mb-4">Available plans</h3>
      <div className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-100 p-1 mb-5" data-testid="billing-cycle-toggle">
        <button onClick={() => setCycle("monthly")} data-testid="cycle-monthly"
          className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${cycle === "monthly" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Monthly</button>
        <button onClick={() => setCycle("annual")} data-testid="cycle-annual"
          className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${cycle === "annual" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
          Annual <span className="text-emerald-600">· 2 months free</span>
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {d.plans.map((p) => {
          const current = p.id === d.company?.plan_id;
          const annual = p.price * 10;
          return (
            <Card key={p.id} className={`border rounded-xl shadow-sm p-6 ${current ? "border-sky-500 ring-1 ring-sky-500" : "border-slate-200/80"}`} data-testid={`plan-${p.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-bold font-display text-lg">{p.name}</p>
                {current && <span className="text-[10px] bg-sky-500 text-white px-2 py-0.5 rounded-full font-semibold">CURRENT</span>}
              </div>
              {cycle === "annual" ? (
                <div className="mt-3">
                  <p className="text-3xl font-bold font-display">{fmtCurrency(annual)}<span className="text-sm font-normal text-slate-400">/yr</span></p>
                  <p className="text-xs text-emerald-600 font-medium mt-1">Save {fmtCurrency(p.price * 2)} vs monthly</p>
                </div>
              ) : (
                <p className="text-3xl font-bold font-display mt-3">{fmtCurrency(p.price)}<span className="text-sm font-normal text-slate-400">/mo</span></p>
              )}
              <ul className="mt-4 space-y-2">
                {(p.features || []).map((feat) => (
                  <li key={feat} className="flex items-start gap-2 text-sm text-slate-600"><Check className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />{feat}</li>
                ))}
              </ul>
              <Button onClick={() => changePlan(p.id)} disabled={current} className={`w-full mt-5 ${current ? "" : "bg-slate-900 hover:bg-slate-800 text-white"}`} variant={current ? "outline" : "default"} data-testid={`select-plan-${p.id}`}>
                {current ? "Current plan" : "Switch to this plan"}
              </Button>
              <Button onClick={() => payNow(p.id)} variant="outline" className="w-full mt-2 gap-2 border-sky-200 text-sky-700 hover:bg-sky-50" data-testid={`pay-plan-${p.id}`}>
                <CreditCard className="h-4 w-4" /> Pay {cycle === "annual" ? "yearly" : "monthly"} with Razorpay
              </Button>
            </Card>
          );
        })}
      </div>
      <p className="text-xs text-slate-400 mt-6">Online payments are powered by Razorpay (INR). Plan changes without payment update your subscription record instantly.</p>
    </div>
  );
}
